import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePromptDialog } from '../../hooks/usePromptDialog';
import { useCircleAgreementStore } from '../../stores/circle-agreement.store';
import { Z_GATE } from '../../utils/layers';

/**
 * The circle confidentiality agreement — shown once, before the first session
 * (circle or 1:1), and again only if the agreement text is revised.
 *
 * Mounted once at the app root. It is opened by the API client, not by a join
 * button: the server refuses every session-join route with 403
 * `circle_agreement_required` until acceptance is on record, and the interceptor
 * parks that request here.
 *
 * The copy is the approved text from docs/plans/CONFIDENTIALITY-LIABILITY.md §5.1.
 * The final paragraph is deliberate and load-bearing: it converts what would
 * otherwise read as a guarantee ("this is confidential") into a disclosed limit
 * ("we enforce this, but we can't control another person"), while still setting
 * the norm. Do not soften it into a promise.
 */
export default function CircleAgreementModal() {
  const isOpen = useCircleAgreementStore((s) => s.isOpen);
  const isSubmitting = useCircleAgreementStore((s) => s.isSubmitting);
  const error = useCircleAgreementStore((s) => s.error);
  const accept = useCircleAgreementStore((s) => s.accept);
  const decline = useCircleAgreementStore((s) => s.decline);
  const dismiss = useCircleAgreementStore((s) => s.dismiss);

  const [checked, setChecked] = useState(false);
  const { pathname } = useLocation();
  const openedAtRef = useRef<string | null>(null);

  // The dialog contract (hooks/usePromptDialog): focus moves in, Tab stays
  // inside, focus goes back to the opener. It is answered with its buttons
  // only, as before: Escape does nothing. Android Back declines it (P5-1),
  // which declines the parked request, as "Not now" does, except while
  // Continue is saving: then Back holds and the server's answer settles the
  // request (c-065). It IS the gate, so it never steps aside for one.
  const { dialogRef } = usePromptDialog(isOpen, decline, {
    yieldToGate: false,
    dismissable: false,
    onBack: dismiss,
  });

  useEffect(() => {
    if (isOpen) setChecked(false);
  }, [isOpen]);

  // Abandon the parked request if the user leaves the page that made it.
  //
  // This modal is global, so without this it survives a Back press — and then
  // "Continue" would REPLAY the join for a circle the user already backed out of,
  // charging them credits and marking them joined to a session they aren't in.
  useEffect(() => {
    if (!isOpen) {
      openedAtRef.current = null;
      return;
    }
    if (openedAtRef.current === null) {
      openedAtRef.current = pathname;
      return;
    }
    if (openedAtRef.current !== pathname) {
      decline();
    }
  }, [isOpen, pathname, decline]);

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          key="circle-agreement-overlay"
          ref={dialogRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="circle-agreement-title"
          tabIndex={-1}
          data-testid="circle-agreement-modal"
          // Above every sheet that can make a gated request (P7-1).
          style={{ zIndex: Z_GATE }}
          className="fixed inset-0 flex items-center justify-center bg-(--ee-scrim)/60 backdrop-blur-sm p-4 outline-none"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-(--ee-surface) rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto"
          >
            <h2
              id="circle-agreement-title"
              className="text-xl font-bold text-(--ee-tw-gray-800) mb-1 text-center"
            >
              Before your first circle
            </h2>
            <p className="text-sm text-(--ee-tw-gray-500) mb-6 text-center">
              One agreement makes this whole thing possible.
            </p>

            <h3 className="text-lg font-semibold text-(--ee-tw-gray-800) mb-3">
              What&rsquo;s shared here, stays here.
            </h3>

            <ul className="space-y-3 text-sm text-(--ee-tw-gray-600) mb-5">
              <li>
                <span className="font-semibold text-(--ee-tw-gray-800)">
                  Don&rsquo;t repeat what others share.
                </span>{' '}
                Not their story, not their name, not identifying details. Not to friends, not on
                social media, not anywhere outside the circle.
              </li>
              <li>
                <span className="font-semibold text-(--ee-tw-gray-800)">
                  Don&rsquo;t record, screenshot, or transcribe
                </span>{' '}
                a session.
              </li>
              <li>
                <span className="font-semibold text-(--ee-tw-gray-800)">
                  What you share about yourself is always yours
                </span>{' '}
                to share however you like.
              </li>
            </ul>

            <p className="text-sm text-(--ee-tw-gray-600) mb-5">
              This is what lets people be honest here. Breaking it can get your account suspended or
              removed.
            </p>

            <p className="text-sm text-(--ee-tw-gray-600) bg-(--ee-tw-gray-50-bg) border border-(--ee-tw-gray-200-line) rounded-xl p-4 mb-6">
              <span className="font-semibold text-(--ee-tw-gray-800)">And one honest thing:</span>{' '}
              we build for confidentiality and we enforce this agreement, but we can&rsquo;t control
              what another person does with what they hear. Please share what you&rsquo;re
              comfortable sharing.
            </p>

            {error ? (
              <p
                data-testid="circle-agreement-error"
                role="alert"
                className="text-sm text-(--ee-tw-red-700) bg-(--ee-tw-red-50-bg) border border-(--ee-tw-red-200-line) rounded-xl p-3 mb-4"
              >
                {error}
              </p>
            ) : null}

            <label className="flex items-start gap-3 cursor-pointer mb-6">
              <input
                type="checkbox"
                data-testid="circle-agreement-checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                className="checkbox checkbox-primary mt-0.5"
              />
              <span className="font-semibold text-(--ee-tw-gray-800)">I understand and agree.</span>
            </label>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={decline}
                disabled={isSubmitting}
                className="flex-1 py-2 text-(--ee-tw-gray-500) hover:text-(--ee-tw-gray-700) text-sm transition-colors"
              >
                Not now
              </button>
              <button
                type="button"
                data-testid="circle-agreement-continue"
                onClick={() => void accept()}
                disabled={!checked || isSubmitting}
                className="flex-1 btn btn-primary"
              >
                {isSubmitting ? 'Saving…' : 'Continue'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
